import { ArrowIcon } from "assets/icons";
import cn from "classnames";
import { If } from "components";
import {
  useBoolean,
  useDebouncedValue,
  useOnClickOutside,
  useSelect
} from "hooks";
import { t } from "i18next";
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useRef, useState } from "react";
import { ClipLoader } from "react-spinners";

import { Checkbox } from "../Checkbox";
import { FormField } from "../FormField";
import { Icon } from "../Icon";
import { SelectProps } from "./Select.types";

import styles from "./Select.module.scss";

const Select = ({
  label,
  multiple = false,
  placeholder,
  onChange,
  options,
  errorMessage,
  value,
  disabled,
  isOptionsLoading = false,
  withFilterInput = false,
  onFilterInputChange
}: SelectProps) => {
  const [filterValue, setFilterValue] = useState("");

  const debouncedFilterValue = useDebouncedValue(filterValue, 800);

  const selectRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const {
    value: isOpenSelect,
    toggle: onToggleSelect,
    setTrue: onOpenSelect,
    setFalse: onCloseSelect
  } = useBoolean(false);

  const { selectedOption, onSelectOption } = useSelect({
    options,
    value,
    onChange,
    onSelect: !multiple ? onCloseSelect : () => {},
    multiple
  });

  useOnClickOutside(selectRef, onCloseSelect);

  const onSelectOptionHandled = (optionValue: string) => {
    if (!multiple) onSelectOption(optionValue);
  };

  const filteredOptions = useMemo(() => {
    return withFilterInput
      ? options.filter((option) => {
          const labelText =
            typeof option.label === "string"
              ? t(option.label).toLowerCase()
              : String(option.label).toLowerCase();
          return labelText.includes(debouncedFilterValue.toLowerCase());
        })
      : options;
  }, [withFilterInput, debouncedFilterValue, options]);

  useEffect(() => {
    if (!isOpenSelect) {
      setFilterValue("");
    } else if (withFilterInput) {
      inputRef.current?.focus();
    }
  }, [isOpenSelect, withFilterInput]);

  useEffect(() => {
    onFilterInputChange?.(debouncedFilterValue);
  }, [debouncedFilterValue]);

  return (
    <FormField label={label} errorMessage={errorMessage}>
      <div ref={selectRef} className={styles.select}>
        {withFilterInput && isOpenSelect && !Array.isArray(selectedOption) && (
          <input
            ref={inputRef}
            type="text"
            value={filterValue}
            placeholder={
              value == null ? t("Start typing to search...") : undefined
            }
            onChange={(e) => setFilterValue(e.target.value)}
            style={{
              position: "absolute",
              top: 10,
              bottom: 10,
              left: 14,
              right: 40,
              border: "none",
              outline: "none",
              backgroundColor: "transparent"
            }}
            onClick={(e) => e.stopPropagation()}
          />
        )}

        <div
          className={cn(styles.select__control, {
            [styles["select__control-placeholder"]]:
              selectedOption === undefined ||
              (Array.isArray(selectedOption) && selectedOption.length === 0),
            [styles.select__control__error]: errorMessage,
            [styles.select__control__disabled]: disabled
          })}
          onClick={() => {
            if (!disabled) {
              onToggleSelect();
            }
          }}
        >
          {Array.isArray(selectedOption) ? (
            <span>
              {selectedOption.length > 0
                ? selectedOption
                    .map((option) => t(String(option?.label)))
                    .join(", ")
                : placeholder}

              {withFilterInput && (
                <input
                  ref={inputRef}
                  type="text"
                  value={filterValue}
                  onChange={(e) => setFilterValue(e.target.value)}
                  style={{
                    fontFamily: "inherit",
                    fontSize: "inherit",
                    marginLeft: 5,
                    border: "none",
                    outline: "none",
                    minWidth: "1ch",
                    width: `${filterValue.length * 0.9}ch`
                  }}
                  onFocus={onOpenSelect}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
            </span>
          ) : (
            <span>
              {filterValue
                ? null
                : selectedOption != null
                  ? typeof selectedOption?.label === "string"
                    ? t(selectedOption?.label)
                    : selectedOption?.label
                  : placeholder}
            </span>
          )}

          <Icon
            className={cn(styles["select__control--icon"], {
              [styles["select__control--icon-open"]]: isOpenSelect
            })}
            as={ArrowIcon}
            boxSize={24}
            color="#090914"
          />
        </div>
        {isOpenSelect ? (
          <div className={styles.select__options}>
            <If condition={isOptionsLoading}>
              <div className={styles.select__noOptionsContainer}>
                <ClipLoader size={24} color="#fb3973" />
              </div>
            </If>

            <If
              condition={
                withFilterInput &&
                filterValue &&
                options.length !== 0 &&
                filteredOptions.length === 0
              }
            >
              <div className={styles.select__noOptionsContainer}>
                <p>{t("There are no options matching your search")}</p>
              </div>
            </If>

            {filteredOptions.map((option) => (
              <div
                key={option.value}
                className={cn(styles.select__option, {
                  [styles.select__option__selected]:
                    !multiple && option.value === value
                })}
                onClick={() => onSelectOptionHandled(option.value)}
              >
                {multiple ? (
                  <Checkbox
                    className={styles["select__checkbox-option"]}
                    label={
                      typeof option.label === "string"
                        ? t(option.label)
                        : option.label
                    }
                    id={option.value}
                    checked={
                      Array.isArray(value) && value.includes(option.value)
                    }
                    onChange={(e) => {
                      e.stopPropagation();
                      onSelectOption(option.value);
                      if (withFilterInput) {
                        inputRef.current?.focus();
                        setFilterValue("");
                      }
                    }}
                  />
                ) : (
                  <span className={styles.select__optionContent}>
                    {option.icon}
                    {typeof option.label === "string"
                      ? t(option.label)
                      : option.label}
                  </span>
                )}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </FormField>
  );
};

export default Select;
